import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { AdminUsersAPI, StudiesAPI, UserStudyPermissionsAPI, GCSCredentialsAPI } from '../../api/client';
import Loading from '../../components/Common/Loading';

const AdminDashboardPage = () => {
  const [users, setUsers] = useState([]);
  const [studies, setStudies] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersData, studiesData, permissionsData] = await Promise.all([
        AdminUsersAPI.list(),
        StudiesAPI.list(),
        UserStudyPermissionsAPI.list()
      ]);

      // Handle both paginated and non-paginated responses
      const usersArray = Array.isArray(usersData) ? usersData : (usersData.results || []);
      const studiesArray = Array.isArray(studiesData) ? studiesData : (studiesData.results || []);
      const permissionsArray = Array.isArray(permissionsData) ? permissionsData : (permissionsData.results || []);

      setUsers(usersArray);
      setStudies(studiesArray);
      setPermissions(permissionsArray);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load admin data');
      console.error('Error loading admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openPermissionModal = (user) => {
    setSelectedUser(user);

    // Find existing permissions for this user
    const userPermission = permissions.find(p => p.user === user.id);
    setSelectedStudies(userPermission?.studies || []);
    setShowPermissionModal(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    try {
      setSavingPermissions(true);

      // Find existing permission record
      const existingPermission = permissions.find(p => p.user === selectedUser.id);

      if (existingPermission) {
        // Update existing permission
        await UserStudyPermissionsAPI.update(existingPermission.id, {
          user: selectedUser.id,
          studies: selectedStudies,
          can_download: true,
          can_export: true
        });
      } else {
        // Create new permission
        await UserStudyPermissionsAPI.create({
          user: selectedUser.id,
          studies: selectedStudies,
          can_download: true,
          can_export: true
        });
      }

      // Reload data
      await loadData();
      setShowPermissionModal(false);
      setSelectedUser(null);
      setSelectedStudies([]);
    } catch (err) {
      alert('Failed to save permissions: ' + (err.response?.data?.detail || err.message));
      console.error('Error saving permissions:', err);
    } finally {
      setSavingPermissions(false);
    }
  };

  const toggleStudy = (studyId) => {
    setSelectedStudies(prev =>
      prev.includes(studyId)
        ? prev.filter(id => id !== studyId)
        : [...prev, studyId]
    );
  };

  const getUserStudies = (userId) => {
    const userPermission = permissions.find(p => p.user === userId);
    if (!userPermission) return [];

    return studies.filter(s => userPermission.studies.includes(s.id));
  };

  const handleDownloadCredentials = async (user) => {
    try {
      const response = await GCSCredentialsAPI.downloadForUser(user.id);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${user.username}-bucket-credentials.json`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to download credentials';
      alert(errorMsg);
      console.error('Error downloading credentials:', err);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading message="Loading admin dashboard..." size="page" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading admin dashboard</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Manage users, studies, and permissions
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{users.length}</p>
            </div>
            <svg className="h-12 w-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm text-gray-600">Total Studies</p>
              <p className="text-3xl font-bold text-gray-900">{studies.length}</p>
            </div>
            <svg className="h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm text-gray-600">Active Permissions</p>
              <p className="text-3xl font-bold text-gray-900">{permissions.length}</p>
            </div>
            <svg className="h-12 w-12 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Accessible Studies
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => {
                const userStudies = getUserStudies(user.id);
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {(user.name || user.username).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name || user.username}</div>
                          <div className="text-sm text-gray-500">@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.is_staff ? (
                        <span className="px-2 py-1 text-xs font-medium text-purple-800 bg-purple-100 rounded-full">
                          Admin
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
                          User
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {userStudies.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {userStudies.slice(0, 3).map(study => (
                              <span key={study.id} className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                                {study.name}
                              </span>
                            ))}
                            {userStudies.length > 3 && (
                              <span className="px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
                                +{userStudies.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">No studies assigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-4">
                        <button
                          onClick={() => openPermissionModal(user)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Manage Permissions
                        </button>
                        <button
                          onClick={() => handleDownloadCredentials(user)}
                          className="text-green-600 hover:text-green-800 font-medium"
                          title="Download GCS credentials for this user"
                        >
                          Download Credentials
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Permission Modal */}
      {showPermissionModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Manage Permissions for {selectedUser.name || selectedUser.username}
              </h2>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1">
              <p className="text-sm text-gray-600 mb-4">
                Select the studies this user can access:
              </p>

              <div className="space-y-2">
                {studies.map((study) => (
                  <label
                    key={study.id}
                    className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudies.includes(study.id)}
                      onChange={() => toggleStudy(study.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="font-medium text-gray-900">{study.name}</div>
                      {study.description && (
                        <div className="text-sm text-gray-500">{study.description}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {study.d_folder_count || 0} d-folders
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPermissionModal(false);
                  setSelectedUser(null);
                  setSelectedStudies([]);
                }}
                disabled={savingPermissions}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={savingPermissions}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
              >
                {savingPermissions ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
